/**
 * Voice Chat Service (Main Orchestrator)
 *
 * This service orchestrates the voice chat functionality by:
 * 1. Receiving credentials from REST API
 * 2. Creating appropriate provider instance
 * 3. Connecting to provider (direct WebRTC)
 * 4. Managing state and events
 *
 * Note: Speaker updates are now handled entirely by LiveKit SDK on the frontend.
 */

import { VoiceChatState, VoiceChatCredentials } from '../../types/voice-chat';
import { VoiceChatProvider, Participant } from '../../types/voice-chat-provider';
import { VoiceChatProviderFactory } from './provider-factory';

export class VoiceChatService {
  private provider: VoiceChatProvider | null = null;
  private state: VoiceChatState;
  private stateListeners: Set<(state: VoiceChatState) => void> = new Set();
  private activeSpeakersCheckInterval: NodeJS.Timeout | null = null;
  private micToggleInProgress = false;

  constructor() {
    this.state = this.getInitialState();
  }

  async connect(credentials: VoiceChatCredentials): Promise<void> {
    if (this.state.isConnected && this.provider) {
      console.warn('[VoiceChat] Already connected, skipping reconnection');
      return;
    }

    try {
      const previousMicState = this.state.microphoneEnabled;

      this.updateState({ isConnecting: true, error: null });
      this.stopActiveSpeakersPolling();

      if (this.provider) {
        try {
          await this.provider.disconnect();
          this.provider.destroy();
        } catch (error) {
          console.warn('[VoiceChat] Error disconnecting existing provider:', error);
        }
        this.provider = null;
      }

      this.provider = VoiceChatProviderFactory.create(credentials.provider);
      this.setupProviderEvents();

      await this.provider.connect({
        url: credentials.url,
        token: credentials.token,
        roomName: credentials.roomName
      });

      const micState = previousMicState !== undefined ? previousMicState : true;

      if (this.provider && micState !== undefined) {
        try {
          await this.provider.setMicrophoneEnabled(micState);
        } catch (error) {
          console.warn('[VoiceChat] Error setting initial microphone state:', error);
        }
      }

      this.updateState({
        isConnected: true,
        isConnecting: false,
        microphoneEnabled: micState
      });
    } catch (error) {
      this.updateState({
        isConnecting: false,
        error: error instanceof Error ? error : new Error(String(error))
      });
      throw error;
    }
  }

  async disconnect(): Promise<void> {
    try {
      this.stopActiveSpeakersPolling();
      if (this.provider) {
        await this.provider.disconnect();
        this.provider.destroy();
        this.provider = null;
      }
      this.updateState({
        isConnected: false,
        participants: [],
        activeSpeakers: []
      });
    } catch (error) {
      console.error('[VoiceChat] Error during disconnect:', error);
    }
  }

  async setMicrophoneEnabled(enabled: boolean): Promise<void> {
    if (!this.provider) {
      throw new Error('Not connected');
    }
    try {
      this.micToggleInProgress = true;
      this.updateState({ microphoneEnabled: enabled });
      await this.provider.setMicrophoneEnabled(enabled);
    } catch (error) {
      console.error('[VoiceChat] Error setting microphone:', error);
      this.updateState({ microphoneEnabled: !enabled });
      throw error;
    } finally {
      this.micToggleInProgress = false;
    }
  }

  getState(): VoiceChatState {
    return { ...this.state };
  }

  getParticipants(): Participant[] {
    if (!this.provider) {
      return [];
    }
    return this.provider.getParticipants();
  }

  getActiveSpeakers(): Participant[] {
    if (!this.provider) {
      return [];
    }
    return this.provider.getActiveSpeakers();
  }

  onStateChange(listener: (state: VoiceChatState) => void): () => void {
    this.stateListeners.add(listener);
    return () => this.stateListeners.delete(listener);
  }

  private setupProviderEvents(): void {
    if (!this.provider) {
      return;
    }

    this.provider.on('connected', () => {
      this.updateState({
        isConnected: true,
        participants: this.provider?.getParticipants() || []
      });
    });

    this.provider.on('disconnected', () => {
      this.updateState({ isConnected: false });
    });

    this.provider.on('speaker_changed', (speakers: unknown) => {
      const speakerArray = Array.isArray(speakers) ? (speakers as Participant[]) : [];
      this.updateState({ activeSpeakers: speakerArray });
    });

    this.startActiveSpeakersPolling();

    this.provider.on('participant_joined', (participant: unknown) => {
      const p = participant as Participant;
      const participants = [...this.state.participants];

      const existingIndex = participants.findIndex((existing) => existing.id === p.id);
      if (existingIndex >= 0) {
        participants[existingIndex] = p;
      } else {
        participants.push(p);
      }

      this.updateState({ participants });
    });

    this.provider.on('participant_left', (participant: unknown) => {
      const pp = participant as Participant;
      const participants = this.state.participants.filter((p) => p.id !== pp.id);
      const activeSpeakers = this.state.activeSpeakers.filter((s) => s.id !== pp.id);
      this.updateState({ participants, activeSpeakers });
    });

    this.provider.on('microphone_changed', (data: unknown) => {
      if (this.micToggleInProgress) return;
      const eventData = data as { enabled?: boolean };
      if (typeof eventData.enabled === 'boolean' && this.state.microphoneEnabled !== eventData.enabled) {
        this.updateState({ microphoneEnabled: eventData.enabled });
      }
    });

    this.provider.on('error', (error: unknown) => {
      const err = error instanceof Error ? error : new Error(String(error));
      this.updateState({ error: err });
      console.error('[VoiceChat] Provider error:', err);
    });
  }

  private updateState(updates: Partial<VoiceChatState>): void {
    this.state = { ...this.state, ...updates };
    this.stateListeners.forEach((listener) => {
      try {
        listener(this.state);
      } catch (error) {
        console.error('[VoiceChat] Error in state listener:', error);
      }
    });
  }

  private getInitialState(): VoiceChatState {
    return {
      isConnected: false,
      isConnecting: false,
      error: null,
      participants: [],
      activeSpeakers: [],
      microphoneEnabled: false
    };
  }

  private startActiveSpeakersPolling(): void {
    this.stopActiveSpeakersPolling();

    this.activeSpeakersCheckInterval = setInterval(() => {
      if (this.provider && this.state.isConnected) {
        const currentActiveSpeakers = this.provider.getActiveSpeakers();
        const currentIds = new Set(currentActiveSpeakers.map((s) => s.id));
        const stateIds = new Set(this.state.activeSpeakers.map((s) => s.id));

        if (currentIds.size !== stateIds.size || ![...currentIds].every((id) => stateIds.has(id))) {
          this.updateState({ activeSpeakers: currentActiveSpeakers });
        }
      }
    }, 500);
  }

  private stopActiveSpeakersPolling(): void {
    if (this.activeSpeakersCheckInterval) {
      clearInterval(this.activeSpeakersCheckInterval);
      this.activeSpeakersCheckInterval = null;
    }
  }
}
